import { __decorate, __metadata } from 'tslib';
import * as i0 from '@angular/core';
import { inject, ChangeDetectorRef, numberAttribute, Component, ChangeDetectionStrategy, ViewEncapsulation, Input } from '@angular/core';
import { CSSModifier, CSSComponent } from '@odx/angular/internal';
import { fromElementResize$ } from '@odx/angular/rxjs';
import { untilDestroyed, injectElement, hasChanged } from '@odx/angular/utils';

const CircularProgressSize = {
    AUTO: 'auto',
    SMALL: 'small',
    MEDIUM: 'medium',
    LARGE: 'large',
};

/**
 * Represents a circular progress component.
 * This component displays a circular progress indicator that can be used to show the progress of an operation.
 */
let CircularProgressComponent = class CircularProgressComponent {
    constructor() {
        this.changeDetector = inject(ChangeDetectorRef);
        this.takeUntilDestroyed = untilDestroyed();
        this._value = 0;
        this.viewPortSize = 100;
        this.radius = 0;
        this.normalizedStroke = 0;
        this.circumference = 283;
        this.element = injectElement();
        /**
         * Specifies whether the circular progress is in indeterminate mode.
         * In indeterminate mode, the progress indicator continuously animates without a specific value.
         *
         * @type {boolean}
         * @default false
         */
        this.indeterminate = false;
        /**
         * The width of the stroke for the circular progress component.
         *
         * @type {number}
         * @default 4
         */
        this.stroke = 4;
        /**
         * The size of the circular progress component.
         *
         * @type {CircularProgressSize}
         * @default CircularProgressSize.MEDIUM
         */
        this.size = CircularProgressSize.MEDIUM;
    }
    get ariaValueNow() {
        return this.indeterminate ? null : this.value;
    }
    get progressValue() {
        return this.indeterminate ? this.circumference : Math.floor((1 - this.value / 100) * this.circumference);
    }
    /**
     * Sets and gets the value of the circular progress component.
     *
     * @param {number} value - The value to set. Must be between -1 and 100 (inclusive).
     */
    set value(value) {
        if (value <= 100 && value >= -1) {
            this._value = value;
            this.indeterminate = value === -1;
        }
    }
    /**
     * Gets the value of the circular progress.
     *
     * @returns {number} The current value of the circular progress.
     */
    get value() {
        return this._value;
    }
    ngAfterViewInit() {
        fromElementResize$(this.element.nativeElement)
            .pipe(this.takeUntilDestroyed())
            .subscribe(() => this.updateDimensions());
    }
    ngOnChanges(changes) {
        if (hasChanged(changes, ['stroke', 'size'])) {
            this.updateDimensions();
        }
    }
    updateDimensions() {
        if (this.stroke < 0)
            return;
        const normalizeFactor = this.viewPortSize / (this.element.nativeElement.offsetWidth || 1);
        this.normalizedStroke = normalizeFactor * this.stroke;
        if (normalizeFactor >= 3) {
            this.normalizedStroke = Math.max(0, this.normalizedStroke - normalizeFactor);
        }
        this.radius = Math.max(0, this.viewPortSize / 2 - this.normalizedStroke / 2);
        this.circumference = Math.floor(2 * this.radius * Math.PI);
        this.changeDetector.detectChanges();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CircularProgressComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "16.1.0", version: "18.2.13", type: CircularProgressComponent, isStandalone: true, selector: "odx-circular-progress", inputs: { stroke: ["stroke", "stroke", numberAttribute], size: "size", value: ["value", "value", numberAttribute] }, host: { properties: { "attr.aria-valuenow": "ariaValueNow", "attr.aria-valuemax": "100", "attr.aria-valuemin": "0", "attr.role": "\"meter\"" } }, usesOnChanges: true, ngImport: i0, template: "<svg class=\"odx-circular-progress__inner\" attr.viewBox=\"0 0 {{ viewPortSize }} {{ viewPortSize }}\">\n  <circle class=\"odx-circular-progress__track\" [attr.stroke-width]=\"normalizedStroke\" [attr.r]=\"radius\" cx=\"50%\" cy=\"50%\" />\n  <circle\n    class=\"odx-circular-progress__indicator\"\n    [attr.stroke-dasharray]=\"circumference\"\n    [attr.stroke-dashoffset]=\"progressValue\"\n    [attr.stroke-width]=\"normalizedStroke\"\n    [attr.r]=\"radius\"\n    cx=\"50%\"\n    cy=\"50%\"\n  />\n</svg>\n<ng-content />\n", changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], CircularProgressComponent.prototype, "indeterminate", void 0);
__decorate([
    CSSModifier(),
    __metadata("design:type", String)
], CircularProgressComponent.prototype, "size", void 0);
CircularProgressComponent = __decorate([
    CSSComponent('circular-progress')
], CircularProgressComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CircularProgressComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-circular-progress', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, host: {
                        '[attr.aria-valuenow]': 'ariaValueNow',
                        '[attr.aria-valuemax]': '100',
                        '[attr.aria-valuemin]': '0',
                        '[attr.role]': '"meter"',
                    }, template: "<svg class=\"odx-circular-progress__inner\" attr.viewBox=\"0 0 {{ viewPortSize }} {{ viewPortSize }}\">\n  <circle class=\"odx-circular-progress__track\" [attr.stroke-width]=\"normalizedStroke\" [attr.r]=\"radius\" cx=\"50%\" cy=\"50%\" />\n  <circle\n    class=\"odx-circular-progress__indicator\"\n    [attr.stroke-dasharray]=\"circumference\"\n    [attr.stroke-dashoffset]=\"progressValue\"\n    [attr.stroke-width]=\"normalizedStroke\"\n    [attr.r]=\"radius\"\n    cx=\"50%\"\n    cy=\"50%\"\n  />\n</svg>\n<ng-content />\n" }]
        }], propDecorators: { indeterminate: [], stroke: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], size: [{
                type: Input
            }], value: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }] } });

/**
 * Generated bundle index. Do not edit.
 */

export { CircularProgressComponent, CircularProgressSize };
